import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';
import Skeleton from "react-loading-skeleton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  Tooltip,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Stack,
  Button,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getReportConfig } from './optionsConfig';
import { setShowFilterModal, fetchColumnFilterOptions } from './redux/ReportsSlice';
import { getColumnFilterEndpoints } from './optionsConfig';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import assignIcon from '../../../common/assets/images/reportAssign.png';
import workflowFilterIcon from '../../../common/assets/images/workflow-filter-icon.png';
import HealthInformationManagerAssign from '../HealthInsuranceManager/HealthInformationManagerAssignModal.js';
import {
  getQueueFilterOptions,
  updateQueuePageNumber,
  updateCoderPageNumber,
  getCoderFilterOptions,
} from '../HealthInsuranceManager/redux/HealthInformationManagerSlicer.js';
import { exportReportData } from './redux/ReportsSlice';
import totalOutstanding from "../../../common/assets/images/notification-box.png";
import highPriority from "../../../common/assets/images/highPriority.png";
import calendar from "../../../common/assets/images/calendar.png";
import hourglass from "../../../common/assets/images/hourglass.png";
import usersreport from "../../../common/assets/images/usersreport.png";

/* ═══════════════════════════════════════════════════════════
   COLUMN FILTER CONFIGS
═══════════════════════════════════════════════════════════ */
const reportColumnFilterConfigs = {
  '/outstanding-report': {
    priority: { type: 'list', field: 'priority', colKey: 'priority' },
    episode_identifier: { type: 'list', field: 'episode_identifier', colKey: 'episode_identifier' },
    patient_id: { type: 'list', field: 'patient_id', colKey: 'patient_id' },
    speciality: { type: 'list', field: 'speciality', colKey: 'speciality' },
    current_queue: { type: 'list', field: 'current_queue', colKey: 'current_queue' },
    assign_coder: { type: 'list', field: 'assign_coder', colKey: 'assign_coder' },
    status: { type: 'list', field: 'status', colKey: 'status' },
    days_of_outstanding: {
      type: 'list',
      field: 'days_of_outstanding',
      colKey: 'days_of_outstanding',
    },
    admission_date: {
      type: 'dateRange',
      fromField: 'admission_date_from',
      toField: 'admission_date_to',
    },
    discharge_date: {
      type: 'dateRange',
      fromField: 'discharge_date_from',
      toField: 'discharge_date_to',
    },
    los: { type: 'list', field: 'los', operatorField: 'los_op', inputType: 'number' },
  },
  '/drg-outlier-report': {
    priority: { type: 'list', field: 'priority', colKey: 'priority' },
    episode_id: { type: 'list', field: 'episode_id', colKey: 'episode_id' },
    admission_date: {
      type: 'dateRange',
      fromField: 'admission_date_from',
      toField: 'admission_date_to',
    },
    separation_date: {
      type: 'dateRange',
      fromField: 'separation_date_from',
      toField: 'separation_date_to',
    },
    los: { type: 'list', field: 'los', operatorField: 'los_op', inputType: 'number' },
    speciality: { type: 'list', field: 'speciality', colKey: 'speciality' },
    principal_diagnosis_code: {
      type: 'list',
      field: 'principal_diagnosis_code',
      colKey: 'principal_diagnosis_code',
    },
    drg: { type: 'list', field: 'drg', colKey: 'drg' },
    description: { type: 'list', field: 'description', colKey: 'description' },
    nwau: { type: 'list', field: 'nwau', colKey: 'nwau' },
    revenue: { type: 'list', field: 'revenue', colKey: 'revenue' },
    drgavlos: { type: 'list', field: 'alos', colKey: 'drgavlos' },
    lower_bound: { type: 'list', field: 'lower_bound', colKey: 'lower_bound' },
    upper_bound: { type: 'list', field: 'upper_bound', colKey: 'upper_bound' },
    status: { type: 'list', field: 'status', colKey: 'status' },
    codes_assigned: { type: 'list', field: 'codes_assigned', colKey: 'codes_assigned' },
  },
  '/hac-report': {
    priority: { type: 'list', field: 'priority', colKey: 'priority' },
    episode_id: { type: 'list', field: 'episode_id' },
    admission_date: {
      type: 'dateRange',
      fromField: 'admission_date_from',
      toField: 'admission_date_to',
    },
    discharge_date: {
      type: 'dateRange',
      fromField: 'discharge_date_from',
      toField: 'discharge_date_to',
    },
    los: { type: 'list', field: 'los', operatorField: 'los_op', inputType: 'number' },
    speciality: { type: 'list', field: 'speciality', colKey: 'speciality' },
    drg: { type: 'list', field: 'drg', colKey: 'drg' },
    nwau: { type: 'list', field: 'nwau', colKey: 'nwau' },
    revenue_dollar: { type: 'list', field: 'revenue_dollar', colKey: 'revenue_dollar' },
    hac_code: { type: 'list', field: 'hac_code' },
    hac_description: { type: 'list', field: 'hac_description' },
    status: { type: 'list', field: 'status', colKey: 'status' },
  },
  '/error-drg-report': {
    priority: { type: 'list', field: 'priority', colKey: 'priority' },
    episode_id: { type: 'list', field: 'episode_id', colKey: 'episode_id' },
    episode_type: { type: 'list', field: 'episode_type', colKey: 'episode_type' },
    admission_date: {
      type: 'dateRange',
      fromField: 'admission_date_from',
      toField: 'admission_date_to',
    },
    discharge_date: {
      type: 'dateRange',
      fromField: 'separation_date_from',
      toField: 'separation_date_to',
    },
    speciality: { type: 'list', field: 'speciality', colKey: 'speciality' },
    drg_code: { type: 'list', field: 'drg_code', colKey: 'drg_code' },
    description: { type: 'list', field: 'description', colKey: 'description' },
    principal_diagnosis: {
      type: 'list',
      field: 'principal_diagnosis',
      colKey: 'principal_diagnosis',
    },
    status: { type: 'list', field: 'status', colKey: 'status' },
    los: { type: 'list', field: 'los', operatorField: 'los_op', inputType: 'number' },
    nwau: { type: 'list', field: 'nwau', colKey: 'nwau' },
    revenue_dollar: { type: 'list', field: 'revenue_dollar', colKey: 'revenue_dollar' },
  },
  '/coding-kpis-report': {
    coder: { type: 'list', field: 'coder' },  // ← was 'coder'
    coding_days: { type: 'list-local', field: 'coding_days', operatorField: 'coding_days_operator', inputType: 'number' },  // ← was 'codinging_days'
    total_episode_coded: { type: 'list-local', field: 'total_episodes_coded', operatorField: 'total_episodes_coded_operator', inputType: 'number' },  // ← was 'total_episodes_coded'
    manually_coded: { type: 'list-local', field: 'manually_coded', operatorField: 'manually_coded_operator', inputType: 'number' },
    ai_validation_coded: { type: 'list-local', field: 'ai_validation_coded', operatorField: 'ai_validation_coded_operator', inputType: 'number' },
    average_los: { type: 'list-local', field: 'average_los', operatorField: 'average_los_operator', inputType: 'number' },
    average_nwau: { type: 'list-local', field: 'average_nwau', operatorField: 'average_nwau_operator', inputType: 'number', allowFloat: true },  // ← was 'nwau'
    date_range: { type: 'dateRange', fromField: 'start_date', toField: 'end_date' },
  },
  '/codingkpis-coder-report': {
    episode_id: { type: 'list', field: 'episode_id', colKey: 'episode_id' },
    site: { type: 'list', field: 'site' }, 
    speciality: { type: 'list', field: 'speciality' },
    validation:{type: 'list', field: 'validation'},
    los: { type: 'list', field: 'los', operatorField: 'los_op', inputType: 'number' },
    drg: { type: 'list', field: 'drg' },
    nwau: { type: 'list-local', field: 'nwau',operatorField: 'nwau_operator', inputType: 'number' },
    no_of_codes: { type: 'list-local', field: 'no_of_codes',operatorField: 'no_of_codes_operator', inputType: 'number' },
    discharge_date: {
      type: 'dateRange',
        fromField: 'separation_date_from',
      toField: 'separation_date_to',
    },
 
  },
};

/* ═══════════════════════════════════════════════════════════
   HELPERS  (module-level — never change reference)
═══════════════════════════════════════════════════════════ */
const getLocalColKeys = (config) =>
  Object.entries(config || {})
    .filter(([, c]) => c.type === 'list-local' && !c.inputType)
    .map(([k]) => k);

// ─── FIX 3: corrected isFilterValueActive ─────────────────────────────────
// "number" filters store { value: ["42"], operator: "gt" } in appliedColumnFilters[field]
// After clear, field is reset to [] or "" — both should be treated as inactive
const isFilterValueActive = (val, inputType) => {
  if (val === null || val === undefined || val === '') return false;
  if (inputType === 'number') {
    // val is an array like ["42"], or "" / [] after clear
    return Array.isArray(val) && val.length > 0 && val[0] !== '';
  }
  if (inputType === 'text') return typeof val === 'string' && val.length > 0;
  if (Array.isArray(val)) return val.length > 0;
  return !!val;
};

const getOptionValue = (opt, fieldName) => {
  if (typeof opt !== "object" || opt === null) return opt;

  // STATUS → send status_id
  if (fieldName === "status") {
    return opt.status_id;
  }

  // CURRENT QUEUE → send queue_id
  if (fieldName === "current_queue") {
    return opt.queue_id;
  }

  // ASSIGN CODER → send user_name
  if (fieldName === "assign_coder") {
    return opt.user_id;
  }
  return (
    opt.user_id ??
    opt.value ??
    opt.user_name ??
    opt.queue_name ??
    opt.drg ??
    opt.description ??
    opt.drg_description ??
    opt.speciality
  );
};

const getOptionLabel = (opt, fieldName) => {
  if (typeof opt !== "object" || opt === null) return String(opt);

  // STATUS → display status text
  if (fieldName === "status") {
    return opt.status;
  }

  // CURRENT QUEUE → display queue name
  if (fieldName === "current_queue") {
    return opt.queue_name;
  }

  return (
    opt.user_name ??
    opt.queue_name ??
    opt.drg_code ??
    opt.description ??
    opt.drg_description ??
    opt.label ??
    opt.speciality ??
    ""
  );
};

// ── Pagination footer ─────────────────────────────────────
const PaginationFooter = React.memo(({ totalPages, page, onChange }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', padding: '8px 16px' }}>
    <Pagination
      count={totalPages}
      page={page}
      onChange={(_, newPage) => onChange(newPage)}
      siblingCount={2}
      boundaryCount={1}
      showFirstButton
      showLastButton
      color="primary"
      shape="rounded"
    />
  </Box>
));

// ── NumberRangeFilter ─────────────────────────────────────
const NumberRangeFilter = ({ open, anchorPos, onClose, onApply, currentValue = {},allowFloat = false}) => {
  const ref = useRef(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [operator, setOperator] = useState(currentValue.operator || '');
  const [value, setValue] = useState(currentValue.value || []);

  // Sync when parent re-opens with a previously applied value
  useEffect(() => {
    setOperator(currentValue.operator || '');
    setValue(currentValue.value || []);
  }, [currentValue.operator, currentValue.value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSelectOpen) return;
      if (ref.current && !ref.current.contains(event.target)) {
        const selectMenu = document.querySelector('.MuiMenu-paper');
        if (selectMenu && selectMenu.contains(event.target)) return;
        onClose();
      }
    };
    if (open) {
      // Delay so the opening click doesn't immediately close
      const t = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
      return () => {
        clearTimeout(t);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open, onClose, isSelectOpen]);

  if (!open) return null;

  return (
    <Paper
      ref={ref}
      elevation={6}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: anchorPos?.top,
        left: anchorPos?.left,
        zIndex: 9999,
        width: 260,
        padding: 16,
      }}>
      <Stack spacing={2}>
        <Select
          size="small"
          fullWidth
          value={operator}
          displayEmpty
          onOpen={() => setIsSelectOpen(true)}
          onClose={() => setIsSelectOpen(false)}
          onChange={(e) => setOperator(e.target.value)}
          MenuProps={{
            disablePortal: true,
            PaperProps: { sx: { zIndex: 10000, maxHeight: 300 } },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}>
          <MenuItem value="">
            <em>Select Operator</em>
          </MenuItem>
          <MenuItem value="gt">&gt;</MenuItem>
          <MenuItem value="lt">&lt;</MenuItem>
          <MenuItem value="gte">&gt;=</MenuItem>
          <MenuItem value="lte">&lt;=</MenuItem>
          <MenuItem value="eq">=</MenuItem>
        </Select>

        <TextField
          // type="number"
          type='text'
          size="small"
          fullWidth
          disabled={!operator}
          value={Array.isArray(value) ? value[0] || '' : value || ''}
          // onChange={(e) => setValue(e.target.value !== '' ? [e.target.value] : [])}
             onChange={(e) => {
                // const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                // setValue(onlyNumbers ? [onlyNumbers] : []);
                if (allowFloat) {
                 // allow digits + single dot
                 const raw = e.target.value.replace(/[^0-9.]/g, '');
                 const sanitized = raw.split('.').length > 2 ? raw.slice(0, -1) : raw;
                 setValue(sanitized ? [sanitized] : []);
               } else {
                 // integers only
                 const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                 setValue(onlyNumbers ? [onlyNumbers] : []);
               }
       }}
  
          placeholder="Enter value"
          helperText={!operator ? 'Select operator first' : ''}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setOperator('');
              setValue([]);
              // FIX: pass empty array so isFilterValueActive correctly returns false
              onApply({ operator: '', value: [] });
              onClose();
            }}>
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!operator || !Array.isArray(value) || value.length === 0 || value[0] === ''}
            onClick={() => {
              onApply({ operator, value });
              onClose();
            }}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ── DateRangeFilterDropdown ───────────────────────────────
const DateRangeFilterDropdown = React.forwardRef(
  ({ anchorPos, fromField, toField, currentValue = {}, onApply, onCancel }, ref) => {
    const [fromDate, setFromDate] = useState(currentValue.from || '');
    const [toDate, setToDate] = useState(currentValue.to || '');
    const fromRef = useRef(null);
     const today = new Date().toISOString().split('T')[0];
    useEffect(() => {
      const t = setTimeout(() => {
        try {
          fromRef.current?.showPicker();
        } catch (_) { }
      }, 60);
      return () => clearTimeout(t);
    }, []);

    return (
      <Paper
        ref={ref}
        elevation={6}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: anchorPos.top,
          left: anchorPos.left,
          zIndex: 9999,
          width: 260,
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          padding: '12px',
        }}>
        <Box sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: '#333' }}>Select Date Range</Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 11, mb: 0.3, color: '#666' }}>From</Box>
          <input
            ref={fromRef}
            type="date"
            value={fromDate}
             max={today}
            onChange={(e) => {
              // setFromDate(e.target.value);
              // if (!e.target.value) setToDate('');
              const newFrom = e.target.value;
              setFromDate(newFrom);
              // only clear To if new From is greater than existing To
              if (toDate && newFrom > toDate) {
                setToDate('');
              }
            }}
            style={{
              width: '100%',
              padding: '7px 8px',
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 11, mb: 0.3, color: '#666' }}>To</Box>
          <input
            type="date"
            value={toDate}
            disabled={!fromDate}
            min={fromDate || undefined}
             max={today}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 8px',
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 13,
              backgroundColor: !fromDate ? '#f5f5f5' : '#fff',
              boxSizing: 'border-box',
            }}
          />
        </Box>

        {(fromDate || toDate) && (
          <Box
            onClick={() => {
              setFromDate('');
              setToDate('');
            }}
            sx={{ fontSize: 12, color: '#1976d2', cursor: 'pointer', mt: 0.5, userSelect: 'none' }}>
            ✕ Clear dates
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!fromDate}
            onClick={() =>
              onApply({ from: fromDate || null, to: toDate || null }, fromField, toField)
            }
            sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Paper>
    );
  },
);

// ── TextFilterDropdown ────────────────────────────────────
const TextFilterDropdown = React.forwardRef(
  ({ anchorPos, fieldName, currentValue, onApply, onCancel }, ref) => {
    const [localValue, setLocalValue] = useState(currentValue || '');
    return (
      <Paper
        ref={ref}
        elevation={6}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: anchorPos.top,
          left: anchorPos.left,
          zIndex: 9999,
          width: 230,
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          padding: '12px',
        }}>
        <Box sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: '#333' }}>Filter</Box>
        <input
          type="text"
          placeholder="Type to filter..."
          value={localValue}
          autoFocus
          onChange={(e) => setLocalValue(e.target.value)}
          style={{
            width: '100%',
            padding: '7px 8px',
            border: '1px solid #ccc',
            borderRadius: 4,
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => onApply(fieldName, localValue || null)}
            sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Paper>
    );
  },
);

// ── ListFilterDropdown ────────────────────────────────────
const ListFilterDropdown = React.forwardRef(
  (
    {
      anchorPos,
      fieldName,
      options,
      loading,
      pendingSelected,
      onToggleOption,
      onSearch,
      onScrollBottom,
      onApply,
      onCancel,
      isLocalType,
    },
    ref,
  ) => {
    const [localSearch, setLocalSearch] = useState('');
    const listRef = useRef(null);

    const displayOptions = isLocalType
      ? options.filter((o) => getOptionLabel(o).toLowerCase().includes(localSearch.toLowerCase()))
      : options;

    const handleSearchChange = (val) => {
      setLocalSearch(val);
      if (!isLocalType) onSearch(fieldName, val);
    };

    const handleOptionClick = (opt, fieldName) => {
      const value = getOptionValue(opt, fieldName);
      onToggleOption(fieldName, value);
      if (localSearch) {
        setLocalSearch('');
        if (!isLocalType) onSearch(fieldName, '');
      }
    };

    const handleScroll = () => {
      const el = listRef.current;
      if (!el || isLocalType) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 20 && !loading)
        onScrollBottom(fieldName, localSearch);
    };

    return (
      <Paper
        ref={ref}
        elevation={6}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: anchorPos.top,
          left: anchorPos.left,
          zIndex: 9999,
          width: 240,
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
        }}>
        <Box sx={{ p: 1, borderBottom: '1px solid #eee' }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search..."
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </Box>
        <Box ref={listRef} onScroll={handleScroll} style={{ maxHeight: 150, overflowY: 'auto' }}>
          {!loading && displayOptions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2, color: '#999', fontSize: 13 }}>No options</Box>
          )}
          {displayOptions.map((opt) => {
            const value = getOptionValue(opt, fieldName);
            const label = getOptionLabel(opt, fieldName);
            return (
              <Box
                key={value}
                onClick={() => handleOptionClick(opt, fieldName)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 0.3,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}>
                <Checkbox
                  size="small"
                  checked={pendingSelected.includes(value)}
                  readOnly
                  sx={{ p: 0.5 }}
                />
                <span style={{ fontSize: 13 }}>{label}</span>
              </Box>
            );
          })}
          {loading && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <CircularProgress size={16} />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            p: 1,
            borderTop: '1px solid #eee',
          }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            sx={{ textTransform: 'none', minWidth: 60 }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => onApply(fieldName)}
            sx={{ textTransform: 'none', minWidth: 60 }}>
            Apply
          </Button>
        </Box>
      </Paper>
    );
  },
);

/* ═══════════════════════════════════════════════════════════
   ReportTable
═══════════════════════════════════════════════════════════ */
const ReportTable = ({
  episodes,
  reportPageNumber,
  setReportPageNumber,
  searchText,
  totalPages,
  handleSearchChange,
  isReportLoading,
  isReportError,
  reportExcelLoading,
  onSortChange,
  onColumnFilterApply,
  onReset,
  resetSignal,
  reportData
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const reportPath = location.pathname;

  const colFilterOpts = useSelector((s) => s.reports.columnFilterOptions[reportPath] || {});
  const { pageNumber: usersPageNumber, loading: usersLoading, hasMore: isUserPageTrue } = useSelector(
    (s) => s.healthInsuranceManager.coderFilterOptions,
  );
  const { pageNumber: queuePageNumber, loading: queueLoading, hasMore: isQueuePageTrue } = useSelector(
    (s) => s.healthInsuranceManager.queueFilterOptions,
  );
  const { appliedFilters } = useSelector((s) => s.reports || {});

  const [selectedHimEpisodes, setSelectedHimEpisodes] = useState([]);
  const [assignUserModal, setAssignUserModal] = useState(false);
  const [rowVersions, setRowVersions] = useState({});
  const [sorting, setSorting] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState({
    hcp_line_id: null,
    assigned_user: '',
    queue: '',
  });

  const [openColumn, setOpenColumn] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState({ top: 0, left: 0 });
  const [pendingSelections, setPendingSelections] = useState({});
  const [appliedColumnFilters, setAppliedColumnFilters] = useState({});
  const colFilterDebounce = useRef({});
  const dropdownRef = useRef(null);

  const activeConfig = reportColumnFilterConfigs[reportPath] || {};
  const localColKeys = getLocalColKeys(activeConfig);
  const cfgEndpoints = getColumnFilterEndpoints(reportPath) || {};

  const [accLocalOpts, setAccLocalOpts] = useState(() =>
    Object.fromEntries(localColKeys.map((k) => [k, []])),
  );

  // Reset local state on route change
  useEffect(() => {
    setAccLocalOpts(Object.fromEntries(getLocalColKeys(activeConfig).map((k) => [k, []])));
    setAppliedColumnFilters({});
    setOpenColumn(null);
  }, [reportPath]); // eslint-disable-line

  // Clear column filters when parent resets
  useEffect(() => {
    if (!resetSignal) return;
    setAppliedColumnFilters({});
    setPendingSelections({});
    setOpenColumn(null);
  }, [resetSignal]);

  // Accumulate list-local options from rows
  useEffect(() => {
    if (!Array.isArray(episodes) || episodes.length === 0) return;
    const keys = getLocalColKeys(activeConfig);
    if (!keys.length) return;
    setAccLocalOpts((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        const set = new Set(prev[k] || []);
        episodes.forEach((ep) => {
          const v = ep[k];
          if (v != null && v !== '') set.add(String(v));
        });
        next[k] = Array.from(set).sort();
      });
      return next;
    });
  }, [episodes]); // eslint-disable-line

  const getOptions = (colKey, cfg) => {
    if (!cfg) return [];
    if (cfg.type === 'list-local') return accLocalOpts[colKey] || [];
    if (cfg.type === 'list') return colFilterOpts[cfg.colKey || cfg.field]?.data || [];
    return [];
  };

  const isColLoading = (cfg) => {
    if (cfg?.type !== 'list') return false;
    return colFilterOpts[cfg.colKey || cfg.field]?.loading || false;
  };

  // ── Export ────────────────────────────────────────────────
  const handleReportExport = useCallback(() => {
    const endPointDetails = getReportConfig(reportPath);
    if (!endPointDetails?.exportEndpoint) return;
    const payload = { ...appliedFilters[reportPath] };
    dispatch(
      exportReportData({
        reportPath,
        endpoint: endPointDetails.exportEndpoint,
        method: endPointDetails.method,
        payload,
      }),
    )
      .unwrap()
      .then((res) => {
        const url = res?.data?.body?.download_url || res?.data?.download_url;
        if (url) {
          const a = document.createElement('a');
          a.href = url;
          a.setAttribute('download', 'report.csv');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      })
      .catch(console.error);
  }, [dispatch, appliedFilters, reportPath]);

  // ── Clear single column filter ─────────────────────────────
  const handleClearColumnFilter = useCallback(
    (colKey) => {
      const cfg = activeConfig[colKey];
      if (!cfg) return;
      const { type, field, fromField, toField, operatorField } = cfg;
      setAppliedColumnFilters((prev) => {
        const updated = { ...prev };
        if (type === 'dateRange') {
          updated[fromField] = null;
          updated[toField] = null;
        } else {
          // FIX: always reset to [] for array-type filters so isFilterValueActive returns false
          updated[field] = Array.isArray(prev[field]) ? [] : '';
          // For coding kpis coder column — also clear user_id
          if (field === 'coder') {
            updated['user_id'] = [];
          }
        }
        if (operatorField) updated[operatorField] = '';
        // onColumnFilterApply?.(updated);
        onColumnFilterApply?.({
          ...appliedFilters[reportPath],
          ...updated,
        });
        return updated;
      });
    },
    [activeConfig, onColumnFilterApply],
  );

  // ── Clear all column filters ───────────────────────────────
  const handleClearAllColumnFilters = useCallback(() => {
    setAppliedColumnFilters({});
    setPendingSelections({});
    onColumnFilterApply?.({});
  }, [onColumnFilterApply]);

  const handleDateRangeApply = useCallback(
    ({ from, to }, fromField, toField) => {
      setAppliedColumnFilters((prev) => {
        const updated = { ...prev, [fromField]: from || null, [toField]: to || null };
        // onColumnFilterApply?.(updated);
        onColumnFilterApply?.({
          ...appliedFilters[reportPath],
          ...updated,
        });
        setReportPageNumber(1);
        return updated;
      });
      setOpenColumn(null);
    },
    [onColumnFilterApply, setReportPageNumber],
  );

  // ── FIX 4: handleToggleDropdown wrapped in useCallback ─────
  // Without this, makeFilterHeader rebuilds every render because
  // it captures handleToggleDropdown in its closure
  const handleToggleDropdown = useCallback(
    (colKey, e) => {
      e.stopPropagation();
      const cfg = activeConfig[colKey];
      if (!cfg) return;

      if (openColumn === colKey) {
        setOpenColumn(null);
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownAnchor({
        top: rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - 265),
      });

      if (cfg.type === 'list') {
        const { field } = cfg;
        setPendingSelections((p) => ({
          ...p,
          [field]: Array.isArray(appliedColumnFilters[field])
            ? [...appliedColumnFilters[field]]
            : [],
        }));

        const ck = cfg.colKey || field;
        const endCfg = cfgEndpoints[ck];
        if (endCfg && !colFilterOpts[ck]?.isLoaded && !colFilterOpts[ck]?.loading) {
          dispatch(
            fetchColumnFilterOptions({
              reportPath,
              columnKey: ck,
              endpoint: endCfg.endpoint,
              responseKey: endCfg.responseKey,
              search: '',
              page: 1,
              limit: 10,
            }),
          );
        }
      }

      setOpenColumn(colKey);
    },
    [
      activeConfig,
      openColumn,
      appliedColumnFilters,
      cfgEndpoints,
      colFilterOpts,
      dispatch,
      reportPath,
    ],
  );

  // Click outside → close dropdown
  useEffect(() => {
    if (!openColumn) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenColumn(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openColumn]);
 const getColumnApiField = (field) => {
  if (reportPath === "/drg-outlier-report") {
    const map = {
      drg: "drg_code",
      // drgavlos: "alos",
    };

    return map[field] || field;
  }

  return field;
};
  // ── Apply handlers ─────────────────────────────────────────
 const handleListApply = useCallback(
  (field) => {
    const values = pendingSelections[field] || [];

    const apiField = getColumnApiField(field);

    setAppliedColumnFilters((prev) => {
      const updated = {
        ...prev,
        [field]: values,
      };

      // API payload should use the backend field name
      const apiFilters = {
        ...appliedFilters[reportPath],
        [apiField]: values,
      };

      // Existing special handling
      if (field === "coder") {
        apiFilters.user_id = values;
      }

      onColumnFilterApply?.(apiFilters);

      return updated;
    });

    setOpenColumn(null);
  },
  [pendingSelections, appliedFilters, reportPath, onColumnFilterApply],
);

  const handleNumberApply = useCallback(
    (field, operatorField, data) => {
      const { operator, value } = data;
      // Convert string values to numbers e.g. ["42"] → [42]
      const numericValue = Array.isArray(value)
        ? value.map((v) => (v !== '' ? Number(v) : v)).filter((v) => v !== '')
        : value;
      setAppliedColumnFilters((prev) => {
        const updated = { ...prev, [field]: numericValue, [operatorField]: operator };
        // onColumnFilterApply?.(updated);
        onColumnFilterApply?.({
          ...appliedFilters[reportPath],
          ...updated,
        });
        setReportPageNumber(1);
        return updated;
      });
    },
    [onColumnFilterApply, setReportPageNumber],
  );

  const handleTextApply = useCallback(
    (field, val) => {
      setAppliedColumnFilters((prev) => {
        const updated = { ...prev, [field]: val };
        // onColumnFilterApply?.(updated);
        onColumnFilterApply?.({
          ...appliedFilters[reportPath],
          ...updated,
        });
        return updated;
      });
      setOpenColumn(null);
    },
    [onColumnFilterApply],
  );

  const handleColFilterCancel = useCallback(() => setOpenColumn(null), []);

  const handleColFilterSearch = useCallback(
    (colKey, val) => {
      const cfg = activeConfig[colKey];
      const ck = cfg?.colKey || cfg?.field;
      const endCfg = cfgEndpoints[ck];
      if (!endCfg) return;
      clearTimeout(colFilterDebounce.current[colKey]);
      colFilterDebounce.current[colKey] = setTimeout(() => {
        dispatch(
          fetchColumnFilterOptions({
            reportPath,
            columnKey: ck,
            endpoint: endCfg.endpoint,
            responseKey: endCfg.responseKey,
            search: val || '',
            page: 1,
            limit: 10,
          }),
        );
      }, 400);
    },
    [activeConfig, cfgEndpoints, dispatch, reportPath],
  );

  const handleColFilterScroll = useCallback(
    (colKey, searchVal) => {
      const cfg = activeConfig[colKey];
      const ck = cfg?.colKey || cfg?.field;
      const endCfg = cfgEndpoints[ck];
      const state = colFilterOpts[ck];
      if (!endCfg || !state || state.loading || state.hasMore === false) return;
      dispatch(
        fetchColumnFilterOptions({
          reportPath,
          columnKey: ck,
          endpoint: endCfg.endpoint,
          responseKey: endCfg.responseKey,
          search: searchVal || '',
          page: (state.page || 1) + 1,
          limit: 10,
        }),
      );
    },
    [activeConfig, cfgEndpoints, colFilterOpts, dispatch, reportPath],
  );

  const handleToggleOption = useCallback((field, opt) => {
    setPendingSelections((p) => {
      const cur = p[field] || [];
      return { ...p, [field]: cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt] };
    });
  }, []);
  /* ═══════════════════════════════════════════════════════════ */
  const makeFilterHeader = useCallback(
    (label, colKey) => {
      // HIDE column-wise filter UI entirely for Missing MVDS Report
    if (reportPath === '/missing-mvds-report') {
      return label;
    }

      const cfg = activeConfig[colKey];
      if (!cfg) return label;

      const { type, field, inputType, fromField, toField } = cfg;
      const appliedVal = field ? appliedColumnFilters[field] : null;

      const hasListValue = Array.isArray(appliedVal) && appliedVal.length > 0;

      const hasDateRange =
        type === 'dateRange' && (appliedColumnFilters[fromField] || appliedColumnFilters[toField]);

      // FIX 3 applied here too: number must have a non-empty array with a real value
      const hasNumberValue =
        inputType === 'number' &&
        Array.isArray(appliedVal) &&
        appliedVal.length > 0 &&
        appliedVal[0] !== '';

      const hasTextValue =
        inputType === 'text' && typeof appliedVal === 'string' && appliedVal.length > 0;

      const hasValue = hasListValue || hasDateRange || hasNumberValue || hasTextValue;

      // FIX 2: compute badge content dynamically — not hardcoded '▼'
      let badgeContent = '▼';
      // if (hasListValue) {
      //   badgeContent = '▲'; // shows count e.g. "3"
      // } else
      if (hasDateRange || hasNumberValue || hasTextValue) {
        badgeContent = '▲'; // shows active indicator
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{label}</span>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleToggleDropdown(colKey, e);
            }}
            sx={{
              cursor: 'pointer',
              fontSize: 11,
              px: 0.5,
              borderRadius: '3px',
              background: hasValue ? '#1976d2' : '#e0e0e0',
              color: hasValue ? '#fff' : '#666',
              minWidth: 18,
              height: 18,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}>
            {badgeContent}
          </Box>
          {hasValue && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleClearColumnFilter(colKey);
              }}
              sx={{
                cursor: 'pointer',
                fontSize: 12,
                color: '#fff',
                background: '#ef5350',
                borderRadius: '50%',
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                '&:hover': { background: '#c62828' },
              }}>
              ×
            </Box>
          )}
        </Box>
      );
      // FIX 4 side-effect: removed `openColumn` from deps — opening a dropdown
      // should NOT cause every column header to rebuild
    },
    [activeConfig, appliedColumnFilters, handleToggleDropdown, handleClearColumnFilter],
  );

  // ── Render dropdown ────────────────────────────────────────
  const renderColumnFilterDropdown = () => {
    if (!openColumn || !activeConfig[openColumn]) return null;
    const { type, field, inputType, colKey, fromField, toField, operatorField } =
      activeConfig[openColumn];

    if (type === 'dateRange') {
      return (
        <DateRangeFilterDropdown
          ref={dropdownRef}
          anchorPos={dropdownAnchor}
          fromField={fromField}
          toField={toField}
          currentValue={{
            from: appliedColumnFilters[fromField] || null,
            to: appliedColumnFilters[toField] || null,
          }}
          onApply={(data) => handleDateRangeApply(data, fromField, toField)}
          onCancel={handleColFilterCancel}
        />
      );
    }

    if (inputType === 'number') {
      return (
        <NumberRangeFilter
          open={true}
          anchorPos={dropdownAnchor}
          currentValue={{
            value: appliedColumnFilters[field] || [],
            operator: appliedColumnFilters[operatorField] || '',
          }}
          allowFloat={activeConfig[openColumn]?.allowFloat || false}
          onClose={handleColFilterCancel}
          onApply={(data) => {
            handleNumberApply(field, operatorField, data);
            setOpenColumn(null);
          }}
        />
      );
    }

    if (inputType === 'text') {
      return (
        <TextFilterDropdown
          ref={dropdownRef}
          anchorPos={dropdownAnchor}
          fieldName={field}
          currentValue={appliedColumnFilters[field] || ''}
          onApply={handleTextApply}
          onCancel={handleColFilterCancel}
        />
      );
    }

    return (
      <ListFilterDropdown
        ref={dropdownRef}
        anchorPos={dropdownAnchor}
        fieldName={field}
        options={getOptions(openColumn, activeConfig[openColumn])}
        loading={isColLoading(activeConfig[openColumn])}
        pendingSelected={pendingSelections[field] || []}
        onToggleOption={handleToggleOption}
        onSearch={handleColFilterSearch}
        onScrollBottom={handleColFilterScroll}
        onApply={handleListApply}
        onCancel={handleColFilterCancel}
        isLocalType={type === 'list-local'}
      />
    );
  };

  // ── DRG helper ────────────────────────────────────────────
  const getRowDrg = (ep) => {
    const sel = rowVersions[ep.episode_id];
    return ep.drgs?.find((d) => d.grouper_version === sel) || ep.drgs?.[0];
  };
  const formatDate = (date) => {
  if (!date) return "-";

  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
}
  /* ═══════════════════════════════════════════════════════════
     COLUMN DEFINITIONS  (all wrapped in useMemo)
  ═══════════════════════════════════════════════════════════ */

  const outstandingColumns = useMemo(
    () => [
      {
        accessorKey: 'priority',
        header: 'Priority',
        Header: () => makeFilterHeader('Priority', 'priority'),
        meta: { backendSort: 'priority' },
        Cell: ({ cell }) => {
          const value = cell.getValue() ?? '-';
          const fmt =
            typeof value === 'string' && value.length > 0
              ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
              : null;
          const styleMap = {
            Urgent: { background: '#E5F5F4', color: '#26625E' },
            High: { background: '#FDE8E8', color: '#9B1C1C' },
            Medium: { background: '#FDF6B2', color: '#723B13' },
            Low: { background: '#d6f5d6', color: '#26625E' },
          };
          if (!fmt) return <span>-</span>;
          return (
            <span style={{ borderRadius: '4px', padding: '4px 8px', ...(styleMap[fmt] || {}) }}>
              {fmt}
            </span>
          );
        },
      },
      {
        accessorKey: 'episode_identifier',
        header: 'Episode ID',
        Header: () => makeFilterHeader('Episode ID', 'episode_identifier'),
        meta: { backendSort: 'episode_identifier' },

        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'patient_id',
        header: 'Patient ID',
        Header: () => makeFilterHeader('Patient ID', 'patient_id'),
        meta: { backendSort: 'patient_id' },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'admission_date',
        header: 'Admission Date',
        Header: () => makeFilterHeader('Admission Date', 'admission_date'),
        meta: { backendSort: 'admission_date' },
        // Cell: ({ cell }) => cell.getValue() ?? '-',
        Cell: ({ cell }) => {
  const v = cell.getValue();
  return formatDate(v);
},
      },
      {
        accessorKey: 'discharge_date',
        header: 'Discharge Date',
        Header: () => makeFilterHeader('Discharge Date', 'discharge_date'),
        meta: { backendSort: 'discharge_date' },

        // Cell: ({ cell }) => cell.getValue() ?? '-',
        Cell: ({ cell }) => {
  const v = cell.getValue();
  return formatDate(v);
},
      },
      {
        accessorKey: 'speciality',
        header: 'Speciality',
        Header: () => makeFilterHeader('Speciality', 'speciality'),
        meta: { backendSort: 'speciality' },

        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'current_queue',
        header: 'Current Queue',
        width: '500px',
        Header: () => makeFilterHeader('Current Queue', 'current_queue'),
        meta: { backendSort: 'current_queue' },

        Cell: ({ cell }) => {
          const q = cell.getValue();
          if (!Array.isArray(q) || q.length === 0) return '-';
          const names = q.map((x) => x?.queue_name || x?.display_value).filter(Boolean);
          if (names.length === 0) return '-';
          const text = names.join(', ');
          const visibleItem = names[0];
          const hasMore = names.length > 1;
          return (
            <Tooltip title={text} arrow>
              <span>
                {visibleItem?.slice(0, 20)}
                {hasMore && ` +${names.length - 1}`}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'assign_coder',
        header: 'Assigned Coder',
        Header: () => makeFilterHeader('Assigned Coder', 'assign_coder'),
        meta: { backendSort: 'assign_coder' },

        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 200,
        Header: () => makeFilterHeader('Status', 'status'),
        meta: { backendSort: 'status' },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return "-";

          const list = value.filter((s) => s?.status);
          const topStatus = list?.filter((item) => item?.is_primary)
          const statusText = topStatus?.map((item) => item.status) ?? "-";
          const hasMultiple = list.length > 1;

          // ✅ KEEP SAME COLOR LOGIC (from your Outstanding)
          const dotColor =
            statusText === "Outstanding"
              ? "#FF6B6B"
              : statusText === "AI codes awaiting validation"
                ? "#4CAF50"
                : "";

          return (
            <Tooltip
              title={
                <div style={{ padding: "4px 8px" }}>
                  {list.map((item, index) => (
                    <div key={index}>{item.status}</div>
                  ))}
                </div>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "160px",
                  cursor: "pointer",
                }}
              >
                {/* 🔥 STACKED LAYERS */}
                {hasMultiple &&
                  list.slice(1, 3).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        top: `${(index + 1) * 4}px`,
                        left: `${(index + 1) * 4}px`,
                        width: "100%",
                        height: "32px",
                        borderRadius: "6px",
                        backgroundColor: "#f5f5f5",
                        zIndex: index,
                      }}
                    />
                  ))}

                {/* ✅ TOP STATUS CARD */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#fff",
                    border: "1px solid #D0D5DD",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* 🔴 DOT COLOR */}
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: dotColor,
                      flex: "0 0 auto",
                    }}
                  />

                  {/* TEXT */}
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {statusText}
                  </Box>
                </Box>

                {/* ➕ COUNT BADGE */}
                {list.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-6px",
                      right: "-6px",
                      background: "#6B7280",
                      color: "#fff",
                      fontSize: "10px",
                      borderRadius: "10px",
                      px: "5px",
                      py: "1px",
                    }}
                  >
                    +{list.length - 1}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        },
      },

      {
        accessorKey: 'days_of_outstanding',
        header: 'Days Outstanding',
        Header: () => makeFilterHeader('Days Outstanding', 'days_of_outstanding'),
        meta: { backendSort: 'days_of_outstanding' },

        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'los',
        header: 'LOS',
        Header: () => makeFilterHeader('LOS', 'los'),
        meta: { backendSort: 'los' },

        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'assign_icon',
        header: '',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => (
          <Tooltip title="Assign">
            <img
              src={assignIcon}
              alt="assign"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setSelectedRecord({
                  hcp_line_id: row.original.hcp_line_id,
                  assigned_user: row.original.assign_coder ?? '',
                  queue: row.original.current_queue ?? '',
                });
                setAssignUserModal(true);
                setSelectedHimEpisodes([row.original]);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [makeFilterHeader],
  );

  const errorDRGColumns = useMemo(
    () => [
      {
        accessorKey: 'episode_id',
        header: 'Episode ID',
        Header: () => makeFilterHeader('Episode ID', 'episode_id'),
        meta: { backendSort: 'episode_id' },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'admission_date',
        header: 'Admission Date',
        Header: () => makeFilterHeader('Admission Date', 'admission_date'),
        meta: { backendSort: 'admission_date' },
        // Cell: ({ cell }) => {
        //   const v = cell.getValue();
        //   return v ? new Date(v).toLocaleDateString() : '-';
        // },
        Cell: ({ cell }) => {
       const v = cell.getValue();
       return formatDate(v);
},
      },
      {
        accessorKey: 'discharge_date',
        header: 'Discharge Date',
        Header: () => makeFilterHeader('Discharge Date', 'discharge_date'),
        meta: { backendSort: 'separation_date' },
        // Cell: ({ cell }) => {
        //   const v = cell.getValue();
        //   return v ? new Date(v).toLocaleDateString() : '-';
        // },
        Cell: ({ cell }) => {
       const v = cell.getValue();
       return formatDate(v);
},
      },
      {
        accessorKey: 'los',
        header: 'LOS',
        size: 60,
        Header: () => makeFilterHeader('LOS', 'los'),
        meta: { backendSort: 'los' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'speciality',
        header: 'Speciality',
        Header: () => makeFilterHeader('Speciality', 'speciality'),
        meta: { backendSort: 'speciality' },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'principal_diagnosis',
        header: 'PDx',
        size: 60,
        Header: () => makeFilterHeader('PDx', 'principal_diagnosis'),
        meta: { backendSort: 'principal_diagnosis' },
        Cell: ({ cell }) => {
          const v = String(cell.getValue() ?? '-');
          return (
            <span title={v}>
              {v.slice(0, 25)}
              {v.length > 25 ? '…' : ''}
            </span>
          );
        },
      },
      {
        accessorKey: 'drg_code',
        header: 'DRG',
        Header: () => makeFilterHeader('DRG', 'drg_code'),
        meta: { backendSort: 'drg_code' },
        Cell: ({ row }) => {
          const v = row.original?.drg_code ?? row.original?.drg;
          return v ? (
            <Tooltip title={v} arrow>
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          );
        },
      },
      {
        accessorKey: 'drg_description',
        header: 'DRG Description',
        Header: () => makeFilterHeader('DRG Description', 'description'),
        meta: { backendSort: 'description' },
        Cell: ({ cell }) => {
          const v = String(cell.getValue() ?? '');
          return (
            <Tooltip title={v} arrow>
              <span title={v}>
                {v.slice(0, 15)}
                {v.length > 15 ? '…' : v.length < 2 ? '-' : ''}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'nwau',
        header: 'NWAU',
        Header: () => makeFilterHeader('NWAU', 'nwau'),
        meta: { backendSort: 'nwau' },
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v != null ? Number(v).toFixed(2) : '-';
        },
      },
      {
        accessorKey: 'dollar_revenue',
        header: '$',
        Header: () => makeFilterHeader('$', 'revenue_dollar'),
        meta: { backendSort: 'revenue_dollar' },
        Cell: ({ cell }) => {
          const v = cell.getValue();
          if (!v) return '-';
          const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]+/g, ''));
          return isNaN(n) ? v : `$${n.toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 200,
        Header: () => makeFilterHeader('Status', 'status'),
        meta: { backendSort: 'status' },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return "-";

          const list = value.filter((s) => s?.status);
          const topStatus = list?.filter((item) => item?.is_primary)
          const statusText = topStatus?.map((item) => item.status) ?? "-";
          const hasMultiple = list.length > 1;

          return (
            <Tooltip
              title={
                <div style={{ padding: "4px 8px" }}>
                  {list.map((item, index) => (
                    <div key={index}>{item.status}</div>
                  ))}
                </div>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "160px",
                  cursor: "pointer",
                }}
              >
                {/* stacked layers */}
                {hasMultiple &&
                  list.slice(1, 3).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        top: `${(index + 1) * 4}px`,
                        left: `${(index + 1) * 4}px`,
                        width: "100%",
                        height: "30px",
                        borderRadius: "6px",
                        backgroundColor: "#f5f5f5",
                        zIndex: index,
                      }}
                    />
                  ))}

                {/* top status */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 10,
                    backgroundColor: "#fff",
                    color: "#344054",
                    border: "1px solid #D0D5DD",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "6px 8px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusText}
                </Box>

                {/* +count badge */}
                {list.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-6px",
                      right: "-6px",
                      background: "#6B7280",
                      color: "#fff",
                      fontSize: "10px",
                      borderRadius: "10px",
                      px: "5px",
                      py: "1px",
                    }}
                  >
                    +{list.length - 1}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [makeFilterHeader],
  );
  
  const MissingMvdsColumns = useMemo(
  () => [
    {
      accessorKey: 'episode_id',
      header: 'Episode ID',
      Header: () => makeFilterHeader('Episode ID', 'episode_id'),
      meta: { backendSort: 'episode_id' },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() ?? '-'} arrow>
          <span>{cell.getValue() ?? '-'}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'mrn_urn',
      header: 'MRN / URN',
      Header: () => makeFilterHeader('MRN / URN', 'medical_record_number'),
      meta: { backendSort: 'mrn_urn' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'discharge_date',
      header: 'Discharge Date',
      Header: () => makeFilterHeader('Discharge Date', 'discharge_date'),
      meta: { backendSort: 'discharge_date' },
      // API already returns a pre-formatted string ("28 Aug 2026") — no reparsing needed
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
  accessorKey: 'episode_status',
  header: 'Episode Status',
  size: 220,
  Header: () => makeFilterHeader('Episode Status', 'status'),
  meta: { backendSort: 'episode_status' },
  Cell: ({ cell }) => {
    const value = cell.getValue();
    if (!Array.isArray(value) || value.length === 0) return '-';

    // Last entry is the most specific status (e.g. "Outstanding", "On Hold", "For Review")
    const primary = value[0];

    const styleMap = {
      "Outstanding": { background: '#FDE8E8', color: '#9B1C1C' },
      'On Hold': { background: '#F3F4F6', color: '#374151' },
      'For Review': { background: '#DBEAFE', color: '#1E40AF' },
      'Awaiting Audit': { background: '#DBEAFE', color: '#1E40AF' },
      'Awaiting CDQ Response': { background: '#EDE9FE', color: '#5B21B6' },
      'CDQ For Review': { background: '#EDE9FE', color: '#5B21B6' },
      'Coding Complete': { background: '#D1FAE5', color: '#065F46' },
      'AI codes awaiting validation': { background: '#FEF3C7', color: '#92400E' },
      'Missing Notes': { background: '#EDE9FE', color: '#5B21B6' },
      'Awaiting MVDS': { background: '#EDE9FE', color: '#9B1C1C' },
    };

    // Show first two statuses inline (e.g. "Missing Notes, Outstanding"), with a +N badge for the rest
    const visibleCount = 2;
    const displayText = value.slice(0, visibleCount).join(', ');
    const extraCount = value.length - visibleCount;

    return (
      <Tooltip
        title={
          <div style={{ padding: '4px 8px' }}>
            {value.map((s, i) => <div key={i}>{s}</div>)}
          </div>
        }
        arrow
        placement="top">
        <span
          style={{
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            ...(styleMap[primary] || { background: '#F3F4F6', color: '#374151' }),
          }}>
          {displayText}
          {extraCount > 0 && ` +${extraCount}`}
        </span>
      </Tooltip>
    );
  },
},
   {
  accessorKey: 'missing_notes',
  header: 'Missing Notes',
  meta: { backendSort: 'missing_notes' },
  Header: () => makeFilterHeader('Missing Notes', 'document_type'),
  Cell: ({ cell }) => {
    const value = cell.getValue();
    if (!Array.isArray(value) || value.length === 0) return '-';

    const visibleCount = 2;
    const displayText = value.slice(0, visibleCount).join(', ');
    const extraCount = value.length - visibleCount;

    return (
      <Tooltip
        title={
          <div style={{ padding: '4px 8px' }}>
            {value.map((s, i) => <div key={i}>{s}</div>)}
          </div>
        }
        arrow
        placement="top">
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            maxWidth: '100%',
          }}>
          {displayText}
          {extraCount > 0 && ` +${extraCount}`}
        </span>
      </Tooltip>
    );
  },
},
    {
      accessorKey: 'missing_note_id_date',
      header: 'Missing Note Id. Date',
      meta: { backendSort: 'missing_note_id_date' },
      // Already a formatted string from the API
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
  ],
  [makeFilterHeader],
);

  const drgColumns = useMemo(
    () => [
      {
        accessorKey: 'episode_id',
        header: 'Episode ID',
        Header: () => makeFilterHeader('Episode ID', 'episode_id'),
        meta: { backendSort: 'episode_id' },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'admission_date',
        header: 'Admission Date',
        Header: () => makeFilterHeader('Admission Date', 'admission_date'),
        meta: { backendSort: 'admission_date' },
        // Cell: ({ cell }) => {
        //   const v = cell.getValue();
        //   return v ? new Date(v).toLocaleDateString() : '-';
        // },
        Cell: ({ cell }) => {
       const v = cell.getValue();
       return formatDate(v);
},
      },
      {
        accessorKey: 'separation_date',
        header: 'Discharge Date',
        Header: () => makeFilterHeader('Discharge Date', 'separation_date'),
        meta: { backendSort: 'separation_date' },
        // Cell: ({ cell }) => {
        //   const v = cell.getValue();
        //   return v ? new Date(v).toLocaleDateString() : '-';
        // },
          Cell: ({ cell }) => {
         const v = cell.getValue();
         return formatDate(v);
},
      },
      {
        accessorKey: 'los',
        header: 'LOS',
        Header: () => makeFilterHeader('LOS', 'los'),
        meta: { backendSort: 'los' },
      },
      {
        accessorKey: 'speciality',
        header: 'Speciality',
        Header: () => makeFilterHeader('Speciality', 'speciality'),
        meta: { backendSort: 'speciality' },
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'principal_diagnosis_code',
        header: 'PDx',
        Header: () => makeFilterHeader('PDx', 'principal_diagnosis_code'),
        meta: { backendSort: 'principal_diagnosis_code' },
        Cell: ({ cell }) => {
          const v = String(cell.getValue() ?? '-');
          return (
            <span title={v}>
              {v.slice(0, 15)}
              {v.length > 15 ? '…' : v.length < 1 ? '-' : ''}
            </span>
          );
        },
      },
      {
        accessorKey: 'drg',
        header: 'DRG',
        Header: () => makeFilterHeader('DRG', 'drg'),
        meta: { backendSort: 'drg_code' },
        Cell: ({ row }) => {
          const ep = row.original;
          const selected = rowVersions[ep.episode_id] ?? ep.drgs?.[0]?.grouper_version;
          return (
            <select
              value={selected}
              onChange={(e) =>
                setRowVersions((p) => ({ ...p, [ep.episode_id]: Number(e.target.value) }))
              }
              style={{
                padding: '2px 4px', display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid #D0D5DD',
                padding: '4px 8px',
                boxShadow: '0px 1px 2px 0px rgba(16,24,40,0.05)',
                borderRadius: '4px',
                maxWidth: '180px',
                overflow: 'hidden',
              }}>
              {[...(ep.drgs || [])]
                .sort((a, b) => (b.is_default === true) - (a.is_default === true))
                .map((d) => (
                  <option key={d.grouper_version} value={d.grouper_version}>
                    {d.drg_code} (v{d.grouper_version})
                  </option>
                ))}
            </select>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'DRG Description',
        Header: () => makeFilterHeader('DRG Description', 'description'),
        meta: { backendSort: 'description' },
        // Cell: ({ row }) => getRowDrg(row.original)?.drg_description || '-',
        Cell: ({ row }) => (
          <Tooltip title={getRowDrg(row.original)?.drg_description ?? '-'} arrow>
            <span>{getRowDrg(row.original)?.drg_description ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'nwau',
        header: 'NWAU',
        Header: () => makeFilterHeader('NWAU', 'nwau'),
        meta: { backendSort: 'nwau' },
        Cell: ({ row }) => {
          const v = getRowDrg(row.original)?.weight;
          return v != null ? Number(v).toFixed(2) : '-';
        },
      },
      {
        accessorKey: 'revenue',
        header: '$',
        Header: () => makeFilterHeader('$', 'revenue'),
        meta: { backendSort: 'revenue' },
        Cell: ({ row }) => {
          const v = getRowDrg(row.original)?.revenue;

          if (!v) return '-';

          // remove $ and commas before converting
          const amount =
            typeof v === "string"
              ? Number(v.replace(/\$/g, "").replace(/,/g, ""))
              : Number(v);

          return !isNaN(amount)
            ? `$${amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
            : "-";
        },
      },
      {
        accessorKey: 'drgavlos',
        header: 'DRG ALOS',
        Header: () => makeFilterHeader('DRG ALOS', 'drgavlos'),
        meta: { backendSort: 'drg_alos' },
        Cell: ({ row }) => getRowDrg(row.original)?.drg_alos || '-',
      },
      {
        accessorKey: 'lower_bound',
        header: 'Lower Bound',
        Header: () => makeFilterHeader('Lower Bound', 'lower_bound'),
        meta: { backendSort: 'lower_bound' },
        Cell: ({ row }) => getRowDrg(row.original)?.lower_bound || '-',
      },
      {
        accessorKey: 'upper_bound',
        header: 'Upper Bound',
        Header: () => makeFilterHeader('Upper Bound', 'upper_bound'),
        meta: { backendSort: 'upper_bound' },
        Cell: ({ row }) => getRowDrg(row.original)?.upper_bound || '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 200,
        Header: () => makeFilterHeader('Status', 'status'),
        meta: { backendSort: 'status' },
        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return "-";

          const list = value.filter((s) => s?.status);
          const topStatus = list?.filter((item) => item?.is_primary)
          const statusText = topStatus?.map((item) => item.status) ?? "-";
          const hasMultiple = list.length > 1;

          return (
            <Tooltip
              title={
                <div style={{ padding: "4px 8px" }}>
                  {list.map((item, index) => (
                    <div key={index}>{item.status}</div>
                  ))}
                </div>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "160px",
                  cursor: "pointer",
                }}
              >
                {/* stacked layers */}
                {hasMultiple &&
                  list.slice(1, 3).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        top: `${(index + 1) * 4}px`,
                        left: `${(index + 1) * 4}px`,
                        width: "100%",
                        height: "30px",
                        borderRadius: "6px",
                        backgroundColor: "#f5f5f5",
                        zIndex: index,
                      }}
                    />
                  ))}

                {/* top status */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 10,
                    backgroundColor: "#fff",
                    color: "#344054",
                    border: "1px solid #D0D5DD",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "6px 8px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusText}
                </Box>

                {/* +count badge */}
                {list.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-6px",
                      right: "-6px",
                      background: "#6B7280",
                      color: "#fff",
                      fontSize: "10px",
                      borderRadius: "10px",
                      px: "5px",
                      py: "1px",
                    }}
                  >
                    +{list.length - 1}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'codes_assigned',
        header: '# Codes Assigned',
        Header: () => makeFilterHeader('# Codes Assigned', 'codes_assigned'),
        meta: { backendSort: 'codes_assigned' },
      },
    ],
    [makeFilterHeader, rowVersions],
  );

  const hacColumns = useMemo(
    () => [
      {
        accessorKey: 'episode_id',
        header: 'Episode ID',
        Header: () => makeFilterHeader('Episode ID', 'episode_id'),
        meta: { backendSort: 'episode_id' },

        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'admission_date',
        header: 'Admission Date',
        Header: () => makeFilterHeader('Admission Date', 'admission_date'),
        meta: { backendSort: 'admission_date' },

        // Cell: ({ cell }) => cell.getValue() ?? '-',
        Cell: ({ cell }) => {
  const v = cell.getValue();
  return formatDate(v);
},
      },
      {
        accessorKey: 'discharge_date',
        header: 'Discharge Date',
        Header: () => makeFilterHeader('Discharge Date', 'discharge_date'),
        meta: { backendSort: 'discharge_date' },

        // Cell: ({ cell }) => cell.getValue() ?? '-',
        Cell: ({ cell }) => {
  const v = cell.getValue();
  return formatDate(v);
},
      },
      {
        accessorKey: 'los',
        header: 'LOS',
        Header: () => makeFilterHeader('LOS', 'los'),
        meta: { backendSort: 'los' },

        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'speciality',
        header: 'Speciality',
        Header: () => makeFilterHeader('Speciality', 'speciality'),
        meta: { backendSort: 'speciality' },

        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'drg',
        header: 'DRG',
        Header: () => makeFilterHeader('DRG', 'drg'),
        meta: { backendSort: 'drg_code' },

        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue() ?? '-'} arrow>
            <span>{cell.getValue() ?? '-'}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'nwau',
        header: 'NWAU',
        Header: () => makeFilterHeader('NWAU', 'nwau'),
        meta: { backendSort: 'nwau' },

        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'revenue_dollar',
        header: '$',
        Header: () => makeFilterHeader('$', 'revenue_dollar'),
        meta: { backendSort: 'revenue_dollar' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
        {
        accessorKey: 'hac_code',
        header: 'HAC Code',
        Header: () => makeFilterHeader('HAC Code', 'hac_code'),
        meta: { backendSort: 'hac_code' },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return '-';

          const text = value.join(', ');

          return (
            <Tooltip title={text} arrow>
              <span>
                {text.length > 20
                  ? `${text.slice(0, 20)}...`
                  : text}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'hac_description',
        header: 'HAC Description',
        Header: () => makeFilterHeader('HAC Description', 'hac_description'),
        meta: { backendSort: 'hac_description' },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return '-';

          const text = value.join(', ');

          return (
            <Tooltip title={text} arrow>
              <span>
                {text.length > 40
                  ? `${text.slice(0, 40)}...`
                  : text}
              </span>
            </Tooltip>
          );
        },
      },


      {
        accessorKey: 'status',
        header: 'Status',
        size: 200,
        Header: () => makeFilterHeader('Status', 'status'),
        meta: { backendSort: 'status' },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!Array.isArray(value) || value.length === 0) return "-";

          const list = value.filter((s) => s?.status);
          const topStatus = list?.filter((item) => item?.is_primary)
          const statusText = topStatus?.map((item) => item.status) ?? "-";
          const hasMultiple = list.length > 1;

          return (
            <Tooltip
              title={
                <div style={{ padding: "4px 8px" }}>
                  {list.map((item, index) => (
                    <div key={index}>{item.status}</div>
                  ))}
                </div>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "160px",
                  cursor: "pointer",
                }}
              >
                {/* stacked layers */}
                {hasMultiple &&
                  list.slice(1, 3).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        top: `${(index + 1) * 4}px`,
                        left: `${(index + 1) * 4}px`,
                        width: "100%",
                        height: "30px",
                        borderRadius: "6px",
                        backgroundColor: "#f5f5f5",
                        zIndex: index,
                      }}
                    />
                  ))}

                {/* top status */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 10,
                    backgroundColor: "#fff",
                    color: "#344054",
                    border: "1px solid #D0D5DD",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "6px 8px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusText}
                </Box>

                {/* +count badge */}
                {list.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-6px",
                      right: "-6px",
                      background: "#6B7280",
                      color: "#fff",
                      fontSize: "10px",
                      borderRadius: "10px",
                      px: "5px",
                      py: "1px",
                    }}
                  >
                    +{list.length - 1}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [makeFilterHeader],
  );
  const codingKpisColumns = useMemo(
    () => [
      {
        accessorKey: 'username',
        header: 'Coder',
        Header: () => makeFilterHeader('Coder', 'coder'),
        meta: { backendSort: 'username' },
        Cell: ({ cell, row }) => {
          const v = cell.getValue() ?? '-';
          return (
            <Tooltip title={v} arrow>
              <span
                style={{ cursor: 'pointer', color: '#5B2D90', textDecoration: 'underline' }}
                // onClick={() => navigate('/codingKpis-coder-overview', { state: { coderName: v } })}
                onClick={() =>
                navigate('/codingkpis-coder-report', {
                  state: {
                    coderName: v,
                    user_id:   row.original.user_id,   // ← for API filter
                    start_date: appliedFilters['/coding-kpis-report']?.start_date ?? null,
                    end_date:   appliedFilters['/coding-kpis-report']?.end_date   ?? null,
                  },
                })}
                >
                {v}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'coding_days',
        header: 'Coding Days',
        Header: () => makeFilterHeader('Coding Days', 'coding_days'),
        meta: { backendSort: 'coding_days' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'total_episode_coded',
        size: 300,
        header: 'Total Episodes Coded',
        Header: () => makeFilterHeader('Total Episodes Coded', 'total_episode_coded'),
        meta: { backendSort: 'total_episode_coded' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'manually_coded',
        header: 'Manually Coded',
        Header: () => makeFilterHeader('Manually Coded', 'manually_coded'),
        meta: { backendSort: 'manually_coded' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'ai_validation_coded',
        header: 'AI Validated',
        Header: () => makeFilterHeader('AI Validated', 'ai_validation_coded'),
        meta: { backendSort: 'ai_validation_coded' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'average_los',
        header: 'Average LOS',
        Header: () => makeFilterHeader('Average LOS', 'average_los'),
        meta: { backendSort: 'average_los' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'average_nwau',
        header: 'Average NWAU',
        Header: () => makeFilterHeader('Average NWAU', 'average_nwau'),
        meta: { backendSort: 'average_nwau' }, 
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
      {
        accessorKey: 'date_range',
        header: 'Date Range',
        Header: () => makeFilterHeader('Date Range', 'date_range'),
        meta: { backendSort: 'date_range' },
        Cell: ({ cell }) => cell.getValue() ?? '-',
      },
    ],
    [makeFilterHeader, navigate],
  );
  const coderOverviewColumns = useMemo(
  () => [
    {
      accessorKey: 'episode_id',
      header: 'Episode ID',
      Header: () => makeFilterHeader('Episode ID', 'episode_id'),
      meta: { backendSort: 'episode_id' },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() ?? '-'} arrow>
          <span>{cell.getValue() ?? '-'}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'site',
      header: 'Site',
      Header: () => makeFilterHeader('Site', 'site'),
      meta: { backendSort: 'site' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'speciality',
      header: 'Specialty',
      Header: () => makeFilterHeader('Specialty', 'speciality'),
      meta: { backendSort: 'speciality' },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() ?? '-'} arrow>
          <span>{cell.getValue() ?? '-'}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'validation',         // "Manual" or "AI Validation"
      header: 'AI Validation or Manual Coding',
      size:400,
      Header: () => makeFilterHeader('AI Validation or Manual Coding', 'validation'),
      meta: { backendSort: 'validation' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'drg',
      header: 'DRG',
      Header: () => makeFilterHeader('DRG', 'drg'),
      meta: { backendSort: 'drg' },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() ?? '-'} arrow>
          <span>{cell.getValue() ?? '-'}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'no_of_codes',      // check your API response key
      header: 'Number of Codes',
      Header: () => makeFilterHeader('Number of Codes', 'no_of_codes'),
      meta: { backendSort: 'no_of_codes' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'los',
      header: 'LOS',
      Header: () => makeFilterHeader('LOS', 'los'),
      meta: { backendSort: 'los' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'nwau',
      header: 'NWAU',
      Header: () => makeFilterHeader('NWAU', 'nwau'),
      meta: { backendSort: 'nwau' },
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v != null ? `${Number(v).toFixed(1)} NWAU` : '-';
      },
    },
    {
      accessorKey: 'discharge_date',
      header: 'Discharge Date',
      Header: () => makeFilterHeader('Discharge Date', 'discharge_date'),
      meta: { backendSort: 'discharge_date' },
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
  ],
  [makeFilterHeader],
);
  const REPORT_CONFIG = {
    '/outstanding-report': outstandingColumns,
    '/drg-outlier-report': drgColumns,
    '/hac-report': hacColumns,
    '/error-drg-report': errorDRGColumns,
    '/coding-kpis-report': codingKpisColumns,
    '/codingkpis-coder-report': coderOverviewColumns,
    '/missing-mvds-report': MissingMvdsColumns,
  };
  const activeColumns = REPORT_CONFIG[reportPath] || [];

  const table = useMaterialReactTable({
    columns: activeColumns,
    data: episodes ?? [],
    icons: {
      FilterListIcon: () => <TuneIcon />,
    },
    enableGlobalFilter: false,
    manualPagination: true,
    manualSorting: true,
    enableDensityToggle: false,
    initialState: { density: 'compact' },
    pageCount: totalPages,
    enableStickyHeader: true,
    enableColumnFilters: false,
    enableColumnPinning: true,
    enableColumnOrdering: false,
    // enableHiding: false,
     // Remove Show/Hide Columns button only for Missing MVDS
 renderToolbarInternalActions: ({ table }) => (
  <>
    {reportPath !== "/missing-mvds-report" && (
      <MRT_ShowHideColumnsButton table={table} />
    )}

    {/* <MRT_ToggleFiltersButton table={table} /> */}
    {/* <MRT_ToggleDensePaddingButton table={table} /> */}
    <MRT_ToggleFullScreenButton table={table} />
  </>
),
    state: {
      isLoading: isReportLoading,
      error: isReportError,
      pagination: { pageIndex: reportPageNumber - 1, pageSize: 10 },
      globalFilter: searchText,
      sorting,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      setReportPageNumber(1)
      const col = activeColumns.find((c) => c.accessorKey === next[0]?.id);
      onSortChange?.({
        sort_by: col?.meta?.backendSort,
        sort_order: next[0]?.desc ? 'desc' : 'asc',
      });
    },
    enablePagination: false,
    onGlobalFilterChange: handleSearchChange,
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
           {reportPath !== '/coding-kpis-report' && reportPath !== '/codingkpis-coder-report' && (
          <Tooltip title="Filters">
            <IconButton onClick={() => dispatch(setShowFilterModal(true))}>
              {/* <img src={workflowFilterIcon} width={22} alt="filter" /> */}
              <TuneIcon sx={{ fontSize: 25, color: "grey.600", }} />
            </IconButton>
          </Tooltip>
           )}
          <Tooltip title="Export">
            <IconButton onClick={handleReportExport}>
              {reportExcelLoading ? (
                <CircularProgress size={16} />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {reportPath !== '/coding-kpis-report' && reportPath !== '/codingkpis-coder-report' && (
          <Tooltip title="Reset all filters">
            <IconButton
              onClick={() => {
                handleClearAllColumnFilters();
                onReset?.();
              }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          )}
        </Box>
      </Box>
    ),
    muiSearchTextFieldProps: {
      placeholder: 'Search by Episode ID...',
      variant: 'outlined',
      size: 'small',
      InputProps: {
        endAdornment: (
          <IconButton size="small" onClick={() => handleSearchChange('')}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ),
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' },
        '& tr:hover': { backgroundColor: '#f0f7ff' },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#f2f6fc !important',
        '& .Mui-TableHeadCell-Content': { backgroundColor: '#f2f6fc !important' },
        padding: '4px 12px',
      },
    },
    muiTableBodyCellProps: { sx: { padding: '4px 12px' } },
    muiTableHeadRowProps: { sx: { backgroundColor: '#f2f6fc !important' } },
    muiTableContainerProps: {
      sx: {
        maxHeight: 600,
        '& thead th': { position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' },
      },
    },
    muiTopToolbarProps: { sx: { backgroundColor: '#f2f6fc', padding: '8px 16px' } },
    renderBottomToolbarCustomActions: () => (
      <PaginationFooter
        totalPages={totalPages}
        page={reportPageNumber}
        onChange={setReportPageNumber}
      />
    ),
  });

  // Queue/user pagination
  // useEffect(() => {
  //   if (isUserPageTrue && !usersLoading) {
  //     dispatch(updateCoderPageNumber(usersPageNumber + 1));
  //     dispatch(getCoderFilterOptions(usersPageNumber + 1,));
  //   }
  // }, [isUserPageTrue, usersLoading]); // eslint-disable-line
  // useEffect(() => {
  //   if (isQueuePageTrue && !queueLoading) {
  //     dispatch(updateQueuePageNumber(queuePageNumber + 1));
  //     dispatch(getQueueFilterOptions(queuePageNumber + 1));
  //   }
  // }, [isQueuePageTrue, queueLoading]); // eslint-disable-line
  useEffect(() => {
  // usersPageNumber starts at 0 — only paginate once a first page has actually loaded
  if (usersPageNumber > 0 && isUserPageTrue && !usersLoading) {
    dispatch(updateCoderPageNumber(usersPageNumber + 1));
    dispatch(
      getCoderFilterOptions({
        page_number: usersPageNumber + 1,
        limit: 10,
        search: "",
      }),
    );
  }
}, [isUserPageTrue, usersLoading]); // eslint-disable-line

useEffect(() => {
  // queuePageNumber starts at 0 — only paginate once a first page has actually loaded
  if (queuePageNumber > 0 && isQueuePageTrue && !queueLoading) {
    dispatch(updateQueuePageNumber(queuePageNumber + 1));
    dispatch(
      getQueueFilterOptions({
        page_number: queuePageNumber + 1,
        limit: 10,
        search: "",
      }),
    );
  }
}, [isQueuePageTrue, queueLoading]); // eslint-disable-line

  const isFullScreen = table.getState().fullscreen;
  // const cards = reportData?.body?.cards || reportData?.cards || reportData?.data?.cards_data;
  const cards =
  reportPath === "/missing-mvds-report"
    ? reportData
    : (reportData?.body?.cards || reportData?.cards || reportData?.data?.cards_data);

  const Widgets = {
    "/outstanding-report": [
      {
        label: "Total Outstanding",
        count: cards?.total_outstanding_episodes,
        bgColor: "#EFF6FF",
        icon: totalOutstanding,
      },
      {
        label: "High Priority",
        count: cards?.high_priority,
        bgColor: "#FEF2F2",
        icon: highPriority,
      },
      {
        label: "30+ Days Old",
        count: cards?.["30_days_or_old"],
        bgColor: "#FEFCE8",
        icon: calendar,
      },
      {
        label: "Avg Days Outstanding",
        count:
          cards?.avg_days_of_outstanding != null
            ? Math.round(Number(cards.avg_days_of_outstanding)).toLocaleString()
            : "-",
        bgColor: "#ECFDF5",
        icon: hourglass,
      },
      {
        label: "Unassigned",
        count: cards?.unassigned_coder,
        bgColor: "#EBEBEB",
        icon: usersreport,
      },
    ],
    "/error-drg-report": [
      {
        label: "801 DRGs",
        count: cards?.count_801_drg,
        bgColor: "#EFF6FF",
        icon: totalOutstanding,
      },
      {
        label: "960Z DRGs",
        count: cards?.count_960z_drg,
        bgColor: "#FEF2F2",
        icon: highPriority,
      },
      {
        label: "961Z DRGs",
        count: cards?.count_961z_drg,
        bgColor: "#FEFCE8",
        icon: calendar,
      },
      {
        label: "963 DRGs",
        count: cards?.count_963z_drg,
        bgColor: "#ECFDF5",
        icon: hourglass,
      },
    ],

    "/missing-mvds-report": [
  {
    label: "Total Episodes",
    count: cards?.total_episodes,
    bgColor: "#EFF6FF",
    icon: totalOutstanding,
  },
  {
    label: "Discharged Last 7 Days",
    count: cards?.discharged_last_7_days,
    bgColor: "#F5F3FF",
    icon: calendar,
  },
  {
    label: "Discharged Last 30 Days",
    count: cards?.discharged_last_30_days,
    bgColor: "#FEFCE8",
    icon: hourglass,
  },
  {
    label: "Discharged Last 30+ Days",
    count: cards?.discharged_last_30_plus_days,
    bgColor: "#FEF2F2",
    icon: highPriority,
  },
],

    


  }
  return (
    <>
      {Widgets[reportPath] && (
        <div className="d-flex gap-2 mb-3 flex-wrap" style={{ flexShrink: 0 }}>
          {Widgets[reportPath].map((w) => (
            <div key={w.label} className="outstanding-widget col" style={{ background: w.bgColor }}>
              <div>
                <p className="mb-1">{w.label}</p>
                {isReportLoading ? (
                  <Skeleton width={60} />
                ) : (
                  <p className="mb-0 fw-bold">{w.count ?? '-'}</p>
                )}
              </div>

              <div>
                <img src={w.icon} alt={w.label} />
              </div>
            </div>
          ))}
        </div>
      )}
      <Paper className={`outstanding-report-table ${isFullScreen ? 'fullscreen-active' : ''}`}>
        <MaterialReactTable
          table={table}
          enableDensityToggle
          enableColumnOrdering={false}
          columnSizingMode="fit"
          renderEmptyRowsFallback={() => (
            <div style={{ textAlign: 'center', padding: '2rem' }}>No data available</div>
          )}
        />

        {assignUserModal && (
           <HealthInformationManagerAssign
            workflowHimsAssginModal={assignUserModal}
            setWorkflowHimsAssginModal={setAssignUserModal}
            selectedRecord={selectedRecord}
            selectedHimEpisodes={selectedHimEpisodes}
            isAllEpisodesSelected={false}
            handleHimSelectChange={() => {}}
            uncheckedIds={[]}
            setCoderSearchTerm={() => {}}
  />
        )}

        {renderColumnFilterDropdown()}
      </Paper>
    </>
  );
};

export default ReportTable;
